import { useState } from "react";
import { useHistory, useParams } from "react-router-dom";

import useSubmitData from "@/hooks/useSubmitData";
import useRedirect from "@/hooks/useRedirect";
import { confirmTransaction, acceptTermsAndConfirm } from "@/services/api";
import i18n from "@/translate/i18n";
import { formatDocumentMask } from "@/utils";

export function useUserDataForm({
  data,
  setData,
  setError,
  setIsFetching,
  setRetryableError,
}) {
  const { checkDataAndRedirect } = useRedirect();
  const history = useHistory();
  const { id, token } = useParams();
  const { submitData } = useSubmitData(setError, setIsFetching, setData);

  const [documentInstructions, setDocumentInstructions] = useState({
    show: false,
    message: i18n.t("form.documentNumberInfo"),
    type: "info",
  });
  const [emailHasErrors, setEmailHasErrors] = useState(false);
  const [isValidDocument, setIsValidDocument] = useState(
    Boolean(data?.customer?.document_number || data?.document_number),
  );
  const [isValidEmail, setIsValidEmail] = useState(
    Boolean(data?.customer?.email),
  );
  const [acceptedTerms, setAcceptedTerms] = useState(
    Boolean(data?.customer?.terms_accepted_at),
  );

  const initialAcceptedTerms = Boolean(data?.customer?.terms_accepted_at);
  const initialCountryIso =
    data?.customer?.country_iso || data?.customer_country_iso || "";
  const initialDocumentType = (() => {
    const docType =
      data?.customer?.document_type || data?.customer_document_types || "";
    return Array.isArray(docType) ? docType[0] || "" : docType;
  })();
  const initialDocumentNumber =
    data?.customer?.document_number || data?.document_number || "";
  const initialEmail =
    data?.customer?.email || data?.customer_data?.email || "";

  const isDocumentNumberMissingInDeposit =
    Boolean(!initialDocumentNumber) && data?.type === "deposit";
  const isDocumentTypeMissingInDeposit =
    Boolean(!initialDocumentType) && data?.type === "deposit";
  const isEmailMissingInDeposit =
    Boolean(!data?.customer?.email) && data?.type === "deposit";
  const incompleteData =
    isDocumentNumberMissingInDeposit ||
    isDocumentTypeMissingInDeposit ||
    isEmailMissingInDeposit ||
    !initialAcceptedTerms;

  const requestTransaction = async (values) => {
    const dataToSend = { ...data, ...values };
    try {
      setRetryableError(null);
      setIsFetching(true);
      await submitData(dataToSend);
    } catch (error) {
      const originalError = error.message.replace("RETRYABLE_ERROR:", "");
      if (error.message?.startsWith("RETRYABLE_ERROR:")) {
        if (originalError === "invalid document") {
          setRetryableError({
            type: "document",
            title: i18n.t("error.invalidDocumentTitle"),
            message: i18n.t("error.invalidDocumentMessage"),
            action: i18n.t("error.invalidDocumentAction"),
          });
        }
      } else {
        let mappedMessage = originalError;
        if (originalError === "rule-validation-failed: Document is REFUSED")
          mappedMessage = "rule-validation-failed-document-refused";
        if (
          originalError ===
          "rule-validation-failed: customer-blocked | missing-customer-name | Document is REFUSED"
        )
          mappedMessage = "rule-validation-reached-attempts";
        setError(i18n.t([`error.${mappedMessage}`, "error.unspecific"]));
      }
    } finally {
      setIsFetching(false);
    }
  };

  const requestUserData = async (values) => {
    const cleanedDocumentNumber = values.document_number
      ? values.document_number.replace(/\D/g, "")
      : "";
    const shouldUseAcceptTermsAndConfirm =
      data?.type === "deposit" &&
      !initialAcceptedTerms &&
      initialDocumentNumber &&
      initialEmail;

    try {
      setRetryableError(null);
      setIsFetching(true);
      if (shouldUseAcceptTermsAndConfirm) {
        await acceptTermsAndConfirm(id, token, {
          customer_data: { ...values, document_number: cleanedDocumentNumber },
          accepted_terms: values.accepted_terms,
        });
        setData((state) => ({
          ...state,
          customer: {
            ...state.customer,
            terms_accepted_at: new Date().toISOString(),
          },
        }));
        checkDataAndRedirect();
      } else {
        const res = await confirmTransaction(id, token, {
          customer_data: { ...values, document_number: cleanedDocumentNumber },
          accepted_terms: values.accepted_terms,
        });
        setData((state) => ({ ...state, ...res.data }));
      }
    } catch (err) {
      const errorMessage = err?.response?.data?.message;
      if (errorMessage === "invalid document") {
        setRetryableError({
          type: "document",
          title: i18n.t("error.invalidDocumentTitle"),
          message: i18n.t("error.invalidDocumentMessage"),
          action: i18n.t("error.invalidDocumentAction"),
        });
      } else {
        let mappedMessage = errorMessage;
        if (errorMessage === "rule-validation-failed: Document is REFUSED")
          mappedMessage = "rule-validation-failed-document-refused";
        if (
          errorMessage ===
          "rule-validation-failed: customer-blocked | missing-customer-name | Document is REFUSED"
        )
          mappedMessage = "rule-validation-reached-attempts";
        setError(i18n.t([`error.${mappedMessage}`, "error.unspecific"]));
      }
    } finally {
      setIsFetching(false);
    }
  };

  const validateDocumentNumber = (documentNumber) => {
    if (!documentNumber || documentNumber.trim() === "") {
      return {
        isValid: false,
        isEmpty: true,
        message: i18n.t("form.invalidDocument"),
      };
    }
    const cleanedDocument = documentNumber.replace(/\D/g, "");
    return {
      isValid: cleanedDocument.length === 11,
      isEmpty: cleanedDocument.length === 0,
      message:
        cleanedDocument.length === 11
          ? i18n.t("form.documentNumberInfo")
          : i18n.t("form.invalidDocument"),
    };
  };

  const handleDocumentFocus = (setFieldError) => {
    setDocumentInstructions({
      show: true,
      message: i18n.t("form.documentNumberInfo"),
      type: "info",
    });
    setFieldError("document_number", "");
  };

  const handleDocumentBlur = (
    documentNumber,
    setFieldTouched,
    setFieldError,
  ) => {
    setFieldTouched("document_number");
    setFieldError("document_number", "");
    const validation = validateDocumentNumber(documentNumber);
    if (validation.isEmpty) {
      setDocumentInstructions({
        show: true,
        message: validation.message,
        type: "error",
      });
      setFieldError("document_number", validation.message);
    } else if (validation.isValid) {
      setDocumentInstructions({
        show: true,
        message: validation.message,
        type: "info",
      });
    } else {
      setDocumentInstructions({
        show: true,
        message: validation.message,
        type: "error",
      });
      setFieldError("document_number", validation.message);
    }
  };

  const handleEmailFocus = (setFieldError) => {
    setEmailHasErrors(false);
    setFieldError("email", "");
  };

  const validEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleEmailBlur = (emailAddress, setFieldTouched, setFieldError) => {
    setFieldTouched("email");
    setFieldError("email", "");
    if (validEmail(emailAddress)) {
      setEmailHasErrors(false);
    } else {
      setFieldError("email", "error");
      setEmailHasErrors(true);
    }
  };

  const handleDocumentChange = (
    target,
    values,
    setFieldTouched,
    setFieldValue,
  ) => {
    setFieldTouched("document_number", true);
    setIsValidDocument(target.value.replace(/-/g, "").length === 11);
    const documentType = values?.document_type || "cuit-cuil";
    const type = Array.isArray(documentType)
      ? documentType[0] || "cuit-cuil"
      : documentType;
    setFieldValue(
      "document_number",
      formatDocumentMask(target.value, String(type).toLowerCase()),
    );
  };

  return {
    // state
    documentInstructions,
    emailHasErrors,
    isValidDocument,
    isValidEmail,
    acceptedTerms,
    setAcceptedTerms,
    setIsValidEmail,
    // derived
    initialAcceptedTerms,
    initialCountryIso,
    initialDocumentType,
    initialDocumentNumber,
    initialEmail,
    isEmailMissingInDeposit,
    incompleteData,
    // handlers
    requestTransaction,
    requestUserData,
    handleDocumentFocus,
    handleDocumentBlur,
    handleEmailFocus,
    handleEmailBlur,
    handleDocumentChange,
    validEmail,
    // navigation
    history,
    id,
    token,
  };
}
